import { BootstrapDialog } from '@components/shared/BootstrapDialog';
import CloseIcon from '@mui/icons-material/Close';
import {
  alpha,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import { AlertCircle, Bot, FileAudio, Mic, PieChart } from 'lucide-react';

// Entitlement type metadata
const ENTITLEMENT_META = {
  ai_interviews: {
    label: 'Voice Interviews',
    labelLower: 'voice interviews',
    icon: Mic,
  },
  ai_participant_responses: {
    label: 'AI Participant Responses',
    labelLower: 'AI participant responses',
    icon: Bot,
  },
  analysis_units: {
    label: 'Analysis Units',
    labelLower: 'analysis units',
    icon: PieChart,
  },
  transcription_uploads: {
    label: 'Transcription Uploads',
    labelLower: 'transcription uploads',
    icon: FileAudio,
  },
};

/**
 * Modal displayed when a user attempts an action but lacks sufficient entitlements.
 * This is shown when the API returns a 402 INSUFFICIENT error.
 *
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {string} entitlementType - Type of entitlement that was insufficient
 * @param {number} required - Amount required for the action
 * @param {number} available - Amount currently available
 * @param {object} accountRep - Account representative info
 */
function InsufficientEntitlementsModal({
  open,
  onClose,
  entitlementType,
  required,
  available,
  accountRep,
}) {
  const theme = useTheme();

  const meta = ENTITLEMENT_META[entitlementType] || {
    label: 'Resources',
    labelLower: 'resources',
    icon: AlertCircle,
  };
  const Icon = meta.icon;

  const repEmail = accountRep?.email || 'sales@qualz.ai';
  const repName = accountRep?.name || 'Qualz Sales Team';
  const hasCalendar = !!accountRep?.calendar_link;

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div">
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon size={20} color={theme.palette.error.main} />
          </Box>
          <Typography variant="h6">Insufficient {meta.label}</Typography>
        </Box>
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <Divider />

      <DialogContent>
        {/* Main message */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.05),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            mb: 3,
          }}
        >
          <Typography variant="body1">
            This action requires{' '}
            <Typography component="span" fontWeight={600} color="error.main">
              {required}
            </Typography>{' '}
            {meta.labelLower}, but you only have{' '}
            <Typography component="span" fontWeight={600} color="error.main">
              {available}
            </Typography>{' '}
            remaining.
          </Typography>
        </Box>

        {/* Contact info */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <Typography variant="body2" fontWeight={500} mb={1}>
            Contact your account representative to add more capacity.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {repName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link href={`mailto:${repEmail}`} underline="hover">
              {repEmail}
            </Link>
            {accountRep?.phone && (
              <>
                {' • '}
                <Link href={`tel:${accountRep.phone}`} underline="hover">
                  {accountRep.phone}
                </Link>
              </>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        {hasCalendar ? (
          <Button
            variant="contained"
            component={Link}
            href={accountRep.calendar_link}
            target="_blank"
            rel="noopener noreferrer"
          >
            Schedule Meeting
          </Button>
        ) : (
          <Button
            variant="contained"
            component={Link}
            href={`mailto:${repEmail}?subject=Request%20More%20${encodeURIComponent(meta.label)}`}
          >
            Contact Rep
          </Button>
        )}
      </DialogActions>
    </BootstrapDialog>
  );
}

export default InsufficientEntitlementsModal;
